import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { Form, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { CustomerType } from '../../enums/customer-type';
import { OrderStatus } from '../../enums/order-status';
import { mapToSalesReport } from '../../utilities/report-utilities';
import { ReportsService } from '../../service/reports.service';
import { ProgressLoaderService } from '../../service/progress-loader.service';
import { SalesReportItem } from '../../models/common-data-types';
import * as XLSX from 'xlsx';
import { ExportService } from '../../service/export.service';
import { ReportFormatService } from '../../service/report-format.service';
import { NgxTranslateModule } from 'src/app/translate.module';
import { LanguageService } from '../../service/language.service';
import { TranslateService } from '@ngx-translate/core';
import { languages } from '../../constants/languages';
import { translate } from '@ngneat/transloco';
import { showErrorToast } from '../../utilities/toast-utils';
import { MessageService } from 'primeng/api';

interface TypeOption {
  name: string;
  value: string;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, CardModule, ButtonModule, NgxTranslateModule, TableModule, FormsModule, ReactiveFormsModule, CalendarModule, DropdownModule, TabViewModule, DividerModule],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss'
})
export class ReportsComponent {
  @ViewChild('salesReportsTable') salesReportsTable: any;

  reportForm!: FormGroup
  customerTypes: string[] = Object.values(CustomerType);
  orderStatuses: string[] = Object.values(OrderStatus);
  reportItems: SalesReportItem[] = [];

  displayedCustomerTypes: TypeOption[] = []
  displayedOrderStatuses: TypeOption[] = []

  languages: any[] = languages;
  selectedLanguage!: string;

  constructor(private fb: FormBuilder,
    private reportService: ReportsService,
    private progressLoaderService: ProgressLoaderService,
    private exportService: ExportService,
    private messageService: MessageService,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private formatService: ReportFormatService) { }

  ngOnInit() {
    this.initializeForm();
    this.initializeLocale();
    this.createDisplayedCustomerTypes(this.customerTypes);
    this.createDisplayedOrderStatuses(this.orderStatuses);

    
  }

  initializeForm() {
    this.reportForm = this.fb.group({
      customerType: [null, [Validators.required]],
      orderStatus: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]]
    })
  }

  initializeLocale() {
    this.languageService.currentLanguage$.subscribe((language) => {
      this.selectedLanguage = language;
      this.setLanguage(this.selectedLanguage);
    });
  }

  setLanguage(language: string) {
    this.translateService.use(language);
    this.languageService.setLanguage(language);
  }

  searchClicked() {
    this.progressLoaderService.show()
    this.reportService.generateReport(mapToSalesReport(this.reportForm)).subscribe({
      next: value => {
        this.reportItems = value.reportItem
        this.progressLoaderService.hide()
      },
      error: err => {
        showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
        this.progressLoaderService.hide()
      }
    })
  }

  downloadClicked() {
    this.exportToExcel();
  }

  exportToExcel() {
    const tableElement = document.getElementById('salesReportsPage')?.querySelector('.p-datatable-table');
    this.exportService.exportTableToExcel(
      tableElement,
      'Motor Insurance Sales Report ' + new Date().toLocaleString()
    );
  }

  createDisplayedCustomerTypes(customerTypes: string[]) {
    this.displayedCustomerTypes = [];
    customerTypes.forEach(type => {
      this.translateService.get(type).subscribe(translatedValue => {
        this.displayedCustomerTypes.push({
          name: type,
          value: translatedValue
        });
      });
    });
  }

  createDisplayedOrderStatuses(orderStatuses: string[]) {
    this.displayedOrderStatuses = [];
    orderStatuses.forEach(type => {
      this.translateService.get(type).subscribe(translatedValue => {
        this.displayedOrderStatuses.push({
          name: type,
          value: translatedValue
        });
      });
    });
  }

}
